import { useMotionValue, useReducedMotion, useSpring } from 'framer-motion';
import { RefCallback, useCallback, useEffect, useMemo, useRef } from 'react';

import { bizyEyeTrackingManager } from './bizy-eye-tracking';
import { useReduceBizyMotionPreference } from './bizy-motion-preferences';

const EYE_TRACK_SPRING = {
	stiffness: 260,
	damping: 22,
	mass: 0.35,
};

const useFinePointer = (): boolean => {
	return useMemo(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return false;
		}

		return window.matchMedia('(pointer: fine)').matches;
	}, []);
};

type UseBizyEyeTrackingOptions = {
	enabled: boolean;
};

type UseBizyEyeTrackingResult = {
	svgRef: RefCallback<SVGSVGElement>;
	eyeOffsetX: ReturnType<typeof useSpring>;
	eyeOffsetY: ReturnType<typeof useSpring>;
};

export const useBizyEyeTracking = ({ enabled }: UseBizyEyeTrackingOptions): UseBizyEyeTrackingResult => {
	const svgElementRef = useRef<SVGSVGElement | null>(null);
	const subscriberRef = useRef<Parameters<typeof bizyEyeTrackingManager.register>[0] | null>(null);
	const offsetX = useMotionValue(0);
	const offsetY = useMotionValue(0);
	const springX = useSpring(offsetX, EYE_TRACK_SPRING);
	const springY = useSpring(offsetY, EYE_TRACK_SPRING);
	const shouldReduceMotion = useReducedMotion();
	const [reduceBizyMotion] = useReduceBizyMotionPreference();
	const hasFinePointer = useFinePointer();

	const isActive = enabled && hasFinePointer && !shouldReduceMotion && !reduceBizyMotion;

	const unregister = useCallback(() => {
		if (subscriberRef.current) {
			bizyEyeTrackingManager.unregister(subscriberRef.current);
			subscriberRef.current = null;
		}
	}, []);

	const syncRegistration = useCallback(() => {
		unregister();

		if (!isActive) {
			offsetX.set(0);
			offsetY.set(0);
			return;
		}

		const element = svgElementRef.current;
		if (!element) {
			offsetX.set(0);
			offsetY.set(0);
			return;
		}

		const subscriber = {
			element,
			onUpdate: ({ x, y }: { x: number; y: number }) => {
				offsetX.set(x);
				offsetY.set(y);
			},
		};

		bizyEyeTrackingManager.register(subscriber);
		subscriberRef.current = subscriber;
	}, [isActive, offsetX, offsetY, unregister]);

	const svgRef = useCallback(
		(node: SVGSVGElement | null) => {
			svgElementRef.current = node;
			syncRegistration();
		},
		[syncRegistration]
	);

	useEffect(() => {
		syncRegistration();

		return unregister;
	}, [syncRegistration, unregister]);

	return {
		svgRef,
		eyeOffsetX: springX,
		eyeOffsetY: springY,
	};
};
